import { useId } from 'react';
import { Burst } from './Burst.tsx';
import styles from './objects.module.css';

const CX = 120;
const CY = 100;
const TEETH = 24;

/** The outline of a gear: `TEETH` teeth between two radii. */
export function gearPath(inner: number, outer: number, teeth = TEETH): string {
  const pts: string[] = [];
  for (let i = 0; i < teeth * 4; i++) {
    const a = (i / (teeth * 4)) * Math.PI * 2;
    const r = i % 4 === 1 || i % 4 === 2 ? outer : inner;
    pts.push(`${(CX + Math.cos(a) * r).toFixed(1)},${(CY + Math.sin(a) * r).toFixed(1)}`);
  }
  return `M${pts.join(' L')} Z`;
}

/**
 * The vault door: a round hatch with bolts, a toothed wheel that turns a notch for every symbol
 * set, gears that never quite stop, and a lamp that says where things stand.
 */
export function VaultObject({
  filled,
  length,
  locked,
  confirmed,
  everyone,
}: {
  filled: number;
  length: number;
  locked: boolean;
  confirmed: number;
  everyone: number;
}) {
  const id = useId();
  const steel = `${id}-steel`;
  const open = everyone > 0 && confirmed >= everyone;
  const lamp = locked ? 'var(--c-danger)' : open ? 'var(--c-neon-green)' : 'var(--c-neon-amber)';
  return (
    <div className={[styles['hero'], styles['compact']].join(' ')}>
      <svg className={styles['svg']} viewBox="0 0 240 200" aria-hidden="true" focusable="false">
        <defs>
          <radialGradient id={steel} cx="0.35" cy="0.3" r="0.9">
            <stop offset="0" stopColor="#6a7488" />
            <stop offset="0.6" stopColor="#3b4256" />
            <stop offset="1" stopColor="#1f2332" />
          </radialGradient>
        </defs>
        <g className={locked ? styles['jam'] : undefined}>
          <circle cx={CX} cy={CY} r="92" fill={`url(#${steel})`} stroke="#8a929c" strokeWidth="3" />
          {Array.from({ length: 12 }, (_, i) => {
            const a = (i / 12) * Math.PI * 2;
            return (
              <circle
                key={i}
                cx={CX + Math.cos(a) * 82}
                cy={CY + Math.sin(a) * 82}
                r="3.4"
                fill="#9aa3b4"
                stroke="#262b3b"
              />
            );
          })}
          <circle cx={CX} cy={CY} r="70" fill="#1a1e2c" stroke="#5a6476" strokeWidth="2" />
          <g className={styles['gears']} style={{ transformOrigin: `${CX}px ${CY}px` }}>
            <path d={gearPath(56, 64)} fill="#2c3246" stroke="var(--c-brass-dark)" />
          </g>
          <g
            className={styles['wheel']}
            style={{
              transform: `rotate(${(filled * 360) / Math.max(1, length)}deg)`,
              transformOrigin: `${CX}px ${CY}px`,
            }}
          >
            <circle cx={CX} cy={CY} r="46" fill="#3b4256" stroke="var(--c-brass)" strokeWidth="3" />
            {[0, 1, 2, 3, 4, 5].map((i) => {
              const a = (i / 6) * Math.PI * 2;
              return (
                <line
                  key={i}
                  x1={CX}
                  y1={CY}
                  x2={CX + Math.cos(a) * 44}
                  y2={CY + Math.sin(a) * 44}
                  stroke="var(--c-brass-light)"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              );
            })}
            <circle
              cx={CX}
              cy={CY}
              r="14"
              fill="var(--c-brass)"
              stroke="var(--c-brass-light)"
              strokeWidth="2"
            />
          </g>
          <circle cx={CX} cy={CY} r="6" fill={lamp} stroke="var(--c-ink-950)" strokeWidth="1.5" />
          <path
            d={`M${CX - 90} ${CY + 14} h-10 v-28 h10 z M${CX + 90} ${CY + 14} h10 v-28 h-10 z`}
            fill="#59627a"
            stroke="#8a929c"
          />
          {Array.from({ length: length }, (_, i) => (
            <circle
              key={i}
              className={styles['lamp']}
              cx={CX - ((length - 1) * 12) / 2 + i * 12}
              cy={CY + 78 - 0}
              r="3.4"
              fill={i < filled ? 'var(--c-neon-amber)' : '#2b3147'}
              stroke="var(--c-ink-950)"
            />
          ))}
        </g>
        {open ? <Burst key="open" x={CX} y={CY} color="var(--c-neon-green)" reach={46} /> : null}
      </svg>
    </div>
  );
}
